import React, { useState, useEffect, useRef } from "react";
import {
    Box, Typography, Avatar, List, ListItem, ListItemAvatar,
    ListItemText, Divider, InputBase, IconButton, styled, Paper, Menu, MenuItem, CircularProgress
} from "@mui/material";
import {
    Search as SearchIcon, MoreVert as MoreVertIcon, Send as SendIcon,
    DoneAll as DoneAllIcon, Security as SecurityIcon, SupportAgent as SupportAgentIcon
} from "@mui/icons-material";

// Firebase Imports
import { db, auth } from "../firebase";
import {
    collection, addDoc, query, where, orderBy, getDoc, getDocs,
    onSnapshot, serverTimestamp, doc, writeBatch, limit, updateDoc
} from "firebase/firestore";

// --- STYLED COMPONENTS ---
const SidebarContainer = styled(Box)({
    width: 400,
    backgroundColor: "#fff",
    display: "flex",
    flexDirection: "column",
    borderRight: "1px solid #d1d7db",
    height: "100%",
});

const ChatWindow = styled(Box)({
    flex: 1,
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#efeae2",
    backgroundImage: "url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')",
    height: "100%",
});

const ScrollableArea = styled(Box)({
    flex: 1,
    overflowY: "auto",
    '&::-webkit-scrollbar': { width: '6px' },
    '&::-webkit-scrollbar-thumb': { backgroundColor: '#ced0d1' },
});

const MessageBubbleContainer = styled(Box)(({ isMine }) => ({
    display: "flex",
    width: "100%",
    justifyContent: isMine ? "flex-end" : "flex-start",
    marginBottom: 4,
    "&:hover .msg-menu-btn": { opacity: 1 }
}));

const MessageBubble = styled(Paper)(({ isMine, isDeleted }) => ({
    padding: "8px 12px 4px 12px",
    maxWidth: "100%",
    borderRadius: 8,
    backgroundColor: isDeleted ? (isMine ? "#f0f2f5" : "#ffffff") : (isMine ? "#dcf8c6" : "#ffffff"),
    boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
    position: "relative",
    borderTopRightRadius: isMine ? 0 : 8,
    borderTopLeftRadius: isMine ? 8 : 0,
    fontStyle: isDeleted ? "italic" : "normal",
    color: isDeleted ? "#8696a0" : "inherit",
}));

// --- COMPONENT: SIDEBAR LIST ITEM ---
const ParentListItem = ({ parent, isSelected, onSelect }) => {
    return (
        <React.Fragment>
            <ListItem
                button
                onClick={() => onSelect(parent)}
                selected={isSelected}
                sx={{
                    py: 1.5,
                    px: 2,
                    "&.Mui-selected": { bgcolor: "#f0f2f5" },
                    "&:hover": { bgcolor: "#f5f6f6" }
                }}
            >
                <ListItemAvatar sx={{ minWidth: 50 }}>
                    <Avatar sx={{ bgcolor: "#00a884", width: 42, height: 42, fontSize: 16 }}>
                        {parent.name?.charAt(0)}
                    </Avatar>
                </ListItemAvatar>
                <ListItemText
                    primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ fontWeight: parent.unreadCount > 0 ? 700 : 600, fontSize: '0.88rem', color: "#111b21" }}>
                                {parent.studentFullName || "Loading Details..."} ({parent.studentClassName || "..."})
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: parent.unreadCount > 0 ? "#25D366" : "#667781", fontWeight: parent.unreadCount > 0 ? 600 : 400 }}>
                                {parent.lastTime || ""}
                            </Typography>
                        </Box>
                    }
                    secondary={
                        <Box sx={{ mt: 0.2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.1 }}>
                                <Typography variant="caption" noWrap sx={{
                                    maxWidth: '85%',
                                    fontSize: '0.78rem',
                                    color: parent.unreadCount > 0 ? "#111b21" : "#667781",
                                    fontWeight: parent.unreadCount > 0 ? 600 : 400
                                }}>
                                    {parent.lastMsg || "No messages yet"}
                                </Typography>
                                {parent.unreadCount > 0 && (
                                    <Box sx={{
                                        bgcolor: '#25D366', color: '#fff', borderRadius: '50%',
                                        width: 18, height: 18, display: 'flex',
                                        alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 'bold'
                                    }}>
                                        {parent.unreadCount}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    }
                />
            </ListItem>
            <Divider variant="inset" component="li" sx={{ borderColor: "#f2f2f2", ml: "70px" }} />
        </React.Fragment>
    );
};

// --- MAIN CONTROLLER COMPONENT ---
export default function SupportDashboard() {
    const [parents, setParents] = useState([]);
    const [loadingContacts, setLoadingContacts] = useState(true);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedParent, setSelectedParent] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");

    const [menuAnchor, setMenuAnchor] = useState(null);
    const [activeTargetMsg, setActiveTargetMsg] = useState(null);

    const chatEndRef = useRef(null);
    const subscriptionsRef = useRef({});
    const currentUser = auth.currentUser?.uid;

    // Fetch Base Contacts / Parents List
    useEffect(() => {
        const q = query(collection(db, "users"), where("role", "==", "Parent"));

        const unsubscribeParents = onSnapshot(q, async (snapshot) => {
            const rawParents = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Resolve basic details (Student Name & Class mapping information)
            const structuralParents = await Promise.all(rawParents.map(async (parent) => {
                let resolvedName = "Unknown Student";
                let resolvedClass = "Not Admitted";

                if (parent.studentId) {
                    try {
                        const studentSnap = await getDoc(doc(db, "students", parent.studentId));
                        if (studentSnap.exists()) {
                            const sData = studentSnap.data();
                            resolvedName = `${sData.firstname || ""} ${sData.fathername || ""} ${sData.surname || ""}`.trim();
                        }

                        const admQuery = query(
                            collection(db, "admission"),
                            where("studentId", "==", parent.studentId),
                            where("status", "==", "Active"),
                            limit(1)
                        );
                        const admSnap = await getDocs(admQuery);
                        if (!admSnap.empty) {
                            const admData = admSnap.docs[0].data();
                            const classId = admData.classId || admData.class;
                            if (classId) {
                                const courseSnap = await getDoc(doc(db, "courses", classId));
                                resolvedClass = courseSnap.exists() ? courseSnap.data().name : classId;
                            }
                        }
                    } catch (err) {
                        console.error("Cross-collection resolution failure:", err);
                    }
                }

                return {
                    ...parent,
                    studentFullName: resolvedName,
                    studentClassName: resolvedClass,
                    lastMsg: "",
                    lastTime: "",
                    lastActiveTime: 0,
                    unreadCount: 0
                };
            }));

            setParents(structuralParents);
            setLoadingContacts(false);

            // Set up real-time sub-message trackers for metadata extraction
            structuralParents.forEach(p => {
                if (subscriptionsRef.current[p.id]) return; // Stop redundant hooks

                const chatId = currentUser < p.id ? `${currentUser}_${p.id}` : `${p.id}_${currentUser}`;
                const msgQuery = query(collection(db, "chats", chatId, "messages"), orderBy("createdAt", "desc"));

                subscriptionsRef.current[p.id] = onSnapshot(msgQuery, (msgSnapshot) => {
                    setParents(prevParents => {
                        return prevParents.map(oldParent => {
                            if (oldParent.id !== p.id) return oldParent;

                            if (!msgSnapshot.empty) {
                                const latestDoc = msgSnapshot.docs[0].data();
                                const time = latestDoc.createdAt?.toDate();

                                const unreadCount = msgSnapshot.docs.filter(doc => {
                                    const d = doc.data();
                                    return d.senderId !== currentUser && d.status !== "read" && !d.isDeleted;
                                }).length;

                                return {
                                    ...oldParent,
                                    lastMsg: latestDoc.isDeleted ? "🚫 This message was deleted" : (latestDoc.text || "Media message"),
                                    lastTime: time ? formatLastMessageTime(time) : "",
                                    lastActiveTime: time ? time.getTime() : 0,
                                    unreadCount: unreadCount
                                };
                            }
                            return oldParent;
                        });
                    });
                });
            });
        });

        return () => {
            unsubscribeParents();
            Object.values(subscriptionsRef.current).forEach(unsub => unsub());
        };
    }, [currentUser]);

    // Track active selected chat history updates
    useEffect(() => {
        if (!selectedParent || !currentUser) return;

        setLoadingMessages(true);
        const chatId = currentUser < selectedParent.id ? `${currentUser}_${selectedParent.id}` : `${selectedParent.id}_${currentUser}`;
        const q = query(collection(db, "chats", chatId, "messages"), orderBy("createdAt", "asc"));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setMessages(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoadingMessages(false);

            const batch = writeBatch(db);
            let hasUpdates = false;
            snapshot.docs.forEach((msgDoc) => {
                const data = msgDoc.data();
                if (data.senderId !== currentUser && data.status !== "read" && !data.isDeleted) {
                    batch.update(doc(db, "chats", chatId, "messages", msgDoc.id), { status: "read", readAt: serverTimestamp() });
                    hasUpdates = true;
                }
            });
            if (hasUpdates) batch.commit();
        });
        return unsubscribe;
    }, [selectedParent?.id, currentUser]);

    useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

    const handleSend = async () => {
        if (!input.trim() || !selectedParent) return;
        const chatId = currentUser < selectedParent.id ? `${currentUser}_${selectedParent.id}` : `${selectedParent.id}_${currentUser}`;
        const text = input;
        setInput("");
        try {
            await addDoc(collection(db, "chats", chatId, "messages"), {
                text, senderId: currentUser, receiverId: selectedParent.id,
                createdAt: serverTimestamp(), status: "sent", isDeleted: false
            });
        } catch (e) { console.error("Error sending message: ", e); }
    };

    const handleOpenMenu = (event, msg) => {
        setMenuAnchor(event.currentTarget);
        setActiveTargetMsg(msg);
    };

    const handleCloseMenu = () => {
        setMenuAnchor(null);
        setActiveTargetMsg(null);
    };

    const handleDeleteForEveryone = async () => {
        if (!activeTargetMsg || !selectedParent) return;
        const chatId = currentUser < selectedParent.id ? `${currentUser}_${selectedParent.id}` : `${selectedParent.id}_${currentUser}`;
        try {
            await updateDoc(doc(db, "chats", chatId, "messages", activeTargetMsg.id), {
                text: "🚫 This message was deleted",
                isDeleted: true,
                status: "deleted"
            });
        } catch (e) { console.error("Failed to delete message: ", e); }
        handleCloseMenu();
    };

    const handleDeleteForMe = () => {
        if (activeTargetMsg) {
            setMessages(prev => prev.filter(m => m.id !== activeTargetMsg.id));
        }
        handleCloseMenu();
    };

    // Filter list and prioritize active channels to the top
    const sortedAndFilteredParents = parents
        .filter(p => {
            const search = searchQuery.toLowerCase();
            return (
                p.name?.toLowerCase().includes(search) ||
                p.studentFullName?.toLowerCase().includes(search) ||
                p.studentClassName?.toLowerCase().includes(search)
            );
        })
        .sort((a, b) => b.lastActiveTime - a.lastActiveTime);

    const formatLastMessageTime = (timestamp) => {
        if (!timestamp) return "";

        const date = timestamp.toDate
            ? timestamp.toDate()
            : new Date(timestamp);

        const now = new Date();

        // Same day => show time
        if (
            date.getDate() === now.getDate() &&
            date.getMonth() === now.getMonth() &&
            date.getFullYear() === now.getFullYear()
        ) {
            return date.toLocaleTimeString([], {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
            });
        }

        const yesterday = new Date();
        yesterday.setDate(now.getDate() - 1);

        // Yesterday
        if (
            date.getDate() === yesterday.getDate() &&
            date.getMonth() === yesterday.getMonth() &&
            date.getFullYear() === yesterday.getFullYear()
        ) {
            return "Yesterday";
        }

        const diffDays = Math.floor(
            (now.setHours(0, 0, 0, 0) - date.setHours(0, 0, 0, 0)) /
            (1000 * 60 * 60 * 24)
        );

        // Last week
        if (diffDays > 1 && diffDays < 7) {
            return `${diffDays} days ago`;
        }

        // Older dates
        return date.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
        });
    };

    return (
        <Box sx={{ display: "flex", height: "85vh", bgcolor: "#f0f2f5" }}>
            <Paper elevation={4} sx={{ display: "flex", flex: 1, overflow: "hidden", borderRadius: 2 }}>

                {/* SIDEBAR PANEL */}
                <SidebarContainer>
                    <Box sx={{ p: 2, bgcolor: "#fff" }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: "#008069" }}>Support Desk</Typography>
                        <Box sx={{ display: "flex", alignItems: "center", bgcolor: "#f0f2f5", borderRadius: 2, px: 2, height: 40 }}>
                            <SearchIcon sx={{ color: "gray", fontSize: 20 }} />
                            <InputBase
                                placeholder="Search parents, students, classes..."
                                sx={{ ml: 1, flex: 1, fontSize: 14 }}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </Box>
                    </Box>
                    <ScrollableArea>
                        {loadingContacts ? (
                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 4, flexDirection: "column", gap: 1 }}>
                                <CircularProgress size={30} sx={{ color: "#00a884" }} />
                                <Typography variant="caption" sx={{ color: "#667781" }}>Syncing contact streams...</Typography>
                            </Box>
                        ) : (
                            <List sx={{ p: 0 }}>
                                {sortedAndFilteredParents.map(parent => (
                                    <ParentListItem
                                        key={parent.id}
                                        parent={parent}
                                        isSelected={selectedParent?.id === parent.id}
                                        onSelect={setSelectedParent}
                                    />
                                ))}
                            </List>
                        )}
                    </ScrollableArea>
                </SidebarContainer>

                {/* CHAT DISPLAY PORTAL */}
                <ChatWindow>
                    {selectedParent ? (
                        <>
                            {/* Header */}
                            <Box sx={{ p: "10px 16px", bgcolor: "#f0f2f5", display: "flex", alignItems: "center", gap: 2, borderBottom: "1px solid #e0e0e0" }}>
                                <Avatar sx={{ bgcolor: "#00a884" }}>{selectedParent.name?.charAt(0)}</Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{selectedParent.name}</Typography>
                                    <Typography variant="caption" sx={{ color: "#00a884", fontWeight: 700 }}>
                                        Student: {selectedParent.studentFullName || "N/A"} ({selectedParent.studentClassName || "N/A"})
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Message Streams Area */}
                            <ScrollableArea sx={{ p: 3, display: "flex", flexDirection: "column" }}>
                                {loadingMessages ? (
                                    <Box sx={{ display: "flex", flex: 1, justifyContent: "center", alignItems: "center", my: "auto" }}>
                                        <CircularProgress sx={{ color: "#00a884" }} />
                                    </Box>
                                ) : (
                                    messages.map((msg) => {
                                        const isMine = msg.senderId === currentUser;
                                        return (
                                            <MessageBubbleContainer key={msg.id} isMine={isMine}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', maxWidth: '100%', flexDirection: isMine ? 'row' : 'row-reverse', gap: 0.5 }}>

                                                    {!msg.isDeleted && (
                                                        <IconButton
                                                            className="msg-menu-btn"
                                                            size="small"
                                                            onClick={(e) => handleOpenMenu(e, msg)}
                                                            sx={{ opacity: 0, transition: 'opacity 0.2s', p: 0.2, color: '#8696a0' }}
                                                        >
                                                            <MoreVertIcon fontSize="inherit" />
                                                        </IconButton>
                                                    )}

                                                    <MessageBubble isMine={isMine} isDeleted={msg.isDeleted}>
                                                        <Typography sx={{ fontSize: 14.5, wordBreak: "break-word" }}>{msg.text}</Typography>
                                                        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mt: 0.2, gap: 0.5 }}>
                                                            <Typography sx={{ fontSize: 9, color: "#667781" }}>
                                                                {msg.createdAt?.toDate()?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) || "..."}
                                                            </Typography>
                                                            {isMine && !msg.isDeleted && (
                                                                <DoneAllIcon sx={{ fontSize: 15, ml: 0.2, color: msg.status === "read" ? "#53bdeb" : "#8696a0" }} />
                                                            )}
                                                        </Box>
                                                    </MessageBubble>
                                                </Box>
                                            </MessageBubbleContainer>
                                        );
                                    })
                                )}
                                <div ref={chatEndRef} />
                            </ScrollableArea>

                            {/* Options Menu Dropdown */}
                            <Menu
                                anchorEl={menuAnchor}
                                open={Boolean(menuAnchor)}
                                onClose={handleCloseMenu}
                            >
                                <MenuItem onClick={handleDeleteForMe} sx={{ fontSize: 13.5 }}>Delete for me</MenuItem>
                                {activeTargetMsg && activeTargetMsg.senderId === currentUser && (
                                    <MenuItem onClick={handleDeleteForEveryone} sx={{ fontSize: 13.5, color: 'error.main' }}>
                                        Delete for everyone
                                    </MenuItem>
                                )}
                            </Menu>

                            {/* Action Form Bar Footer */}
                            <Box sx={{ p: 2, bgcolor: "#f0f2f5", display: "flex", alignItems: "center", gap: 1.5 }}>
                                <InputBase
                                    fullWidth
                                    placeholder="Write your response..."
                                    sx={{ bgcolor: "#fff", borderRadius: 3, px: 2, py: 1, fontSize: 14, border: "1px solid #e0e0e0" }}
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                                />
                                <IconButton onClick={handleSend} sx={{ bgcolor: "#00a884", color: "#fff", "&:hover": { bgcolor: "#008069" } }}>
                                    <SendIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </>
                    ) : (
                        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)" }}>
                            <Box sx={{ textAlign: 'center', p: 5 }}>
                                <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
                                    <Box sx={{ width: 140, height: 140, borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
                                        <SupportAgentIcon sx={{ fontSize: 70, color: '#00a884' }} />
                                    </Box>
                                    <Box sx={{ position: 'absolute', bottom: 10, right: 0, bgcolor: '#25D366', p: 1, borderRadius: '50%', border: '4px solid #f8f9fa' }}>
                                        <SecurityIcon sx={{ color: '#fff', fontSize: 18 }} />
                                    </Box>
                                </Box>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#111b21', mb: 1 }}>Support Desk Portal</Typography>
                                <Typography variant="body1" sx={{ color: '#667781', maxWidth: 350, mx: 'auto', lineHeight: 1.6 }}>
                                    Select a continuous conversation stream from the sidebar layout to view active messages.
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </ChatWindow>
            </Paper>
        </Box>
    );
}